/**
 * Newsletter Overlay Styles
 * Glass card overlay for landing page newsletter signup
 * Matches existing WaitlistCard + WarningOverlay patterns
 */

import styled, { keyframes } from 'styled-components';
import { GlassCard } from '@components/primitives/glass-card';
import { colors, gradients, gradientToCSS } from '@config/colors';
import { spacing, borderRadius } from '@config/spacing';
import { typography } from '@config/typography';
import { media } from '@config/breakpoints';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${colors.brand.primary}d9;
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${spacing.md};
`;

export const Card = styled(GlassCard)`
  max-width: 420px;
  width: 100%;
  margin: 0 auto;
  padding: ${spacing['2xl']};
  border-radius: ${borderRadius['3xl']};
  background: linear-gradient(
      180deg,
      ${colors.overlayWhite40},
      ${colors.overlayWhite60} 45%,
      ${colors.overlayWhite40}
    ),
    ${colors.overlayWhite60};
  border: 1px solid ${colors.overlayWhite40};
  backdrop-filter: blur(28px);
  box-shadow:
    0 22px 44px rgba(12, 38, 61, 0.16),
    inset 0 1px 0 rgba(255, 255, 255, 0.4);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing.lg};
  text-align: center;
  animation: ${fadeInUp} 0.4s ease-out;
`;

export const Heading = styled.h3`
  margin: 0;
  font-family: ${typography.fontFamily.heading};
  font-size: 22px;
  font-weight: ${typography.fontWeights.medium};
  color: ${colors.accent.coral.extraDark};

  ${media.md} {
    font-size: 24px;
  }
`;

export const Message = styled.p`
  margin: 0;
  font-family: ${typography.fontFamily.body};
  font-size: ${typography.fontSize.base};
  line-height: ${typography.lineHeight.bodyRelaxed};
  color: ${colors.neutral.dark};
`;

export const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
  margin-top: ${spacing.lg};
  margin-bottom: ${spacing.xl};
`;

export const InputLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  text-align: left;
  font-family: ${typography.fontFamily.body};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeights.medium};
  color: ${colors.neutral.dark};
`;

export const Input = styled.input`
  width: 100%;
  padding: ${spacing.md};
  border-radius: ${borderRadius.xl};
  border: 1px solid ${colors.neutral.mediumLight};
  background: ${colors.white};
  font-family: ${typography.fontFamily.body};
  font-size: ${typography.fontSize.base};
  color: ${colors.neutral.extraDark};
  outline: none;
  transition: border-color 200ms ease, box-shadow 200ms ease;
  box-sizing: border-box;

  &::placeholder {
    color: ${colors.neutral.medium};
  }

  &:focus {
    border-color: ${colors.highlight.primary};
    box-shadow: 0 0 0 3px ${colors.seafoamOverlay20};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.sm};
  min-height: 48px;
  width: 100%;
  padding: ${spacing.md} ${spacing.xl};
  border-radius: ${borderRadius['3xl']};
  background: ${gradientToCSS(gradients.accentCoralDark)};
  color: ${colors.neutral.extraLight};
  font-family: ${typography.fontFamily.body};
  font-size: ${typography.fontSize.base};
  font-weight: ${typography.fontWeights.medium};
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(251, 113, 133, 0.3);
  transition: all 200ms ease-in-out;

  &:hover:not(:disabled) {
    background: ${gradientToCSS(gradients.accentCoral)};
    box-shadow: 0 6px 18px rgba(239, 68, 68, 0.4);
    filter: brightness(1.05);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const CloseButton = styled.button`
  position: absolute;
  top: ${spacing.md};
  right: ${spacing.md};
  background: none;
  border: none;
  cursor: pointer;
  color: ${colors.neutral.medium};
  padding: ${spacing.xs};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${borderRadius.full};
  transition: color 200ms ease, background 200ms ease;

  &:hover {
    color: ${colors.neutral.extraDark};
    background: ${colors.overlayWhite40};
  }
`;

export const ErrorText = styled.p`
  margin: 0;
  font-family: ${typography.fontFamily.body};
  font-size: ${typography.fontSize.sm};
  color: ${colors.accent.coral.extraDark};
  line-height: ${typography.lineHeight.relaxed};
`;

export const SuccessMessage = styled.p`
  margin: 0;
  font-family: ${typography.fontFamily.body};
  font-size: ${typography.fontSize.base};
  line-height: ${typography.lineHeight.bodyRelaxed};
  color: ${colors.highlight.extraDark};
  font-weight: ${typography.fontWeights.medium};
`;

export const Links = styled.div`
  display: flex;
  gap: ${spacing.lg};
  justify-content: center;
`;

export const LinkButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.xs};
  font-family: ${typography.fontFamily.body};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeights.medium};
  color: ${colors.accent.coral.extraDark};
  text-decoration: none;
  transition: opacity 0.2s ease;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
    text-decoration: underline;
  }
`;
